import { collection, addDoc, query, where, getDocs, serverTimestamp, doc, setDoc, getDoc, increment, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { sendQuizConfirmationEmail, sendIndustrialVisitConfirmationEmail, sendDucatVisitConfirmationEmail } from './emailService';


const IV_COLLECTION = 'industrialVisitRegistrations';

/**
 * Get event capacity from Firebase settings
 * If dayName is provided, returns eventCapacities[dayName] fallback to global eventCapacity.
 * @param {string} [dayName]
 * @returns {Promise<number>} The capacity (defaults to 55 if not set)
 */
export const getEventCapacity = async (dayName) => {
  try {
    console.log('Fetching event capacity from Firebase settings...');
    const settingsRef = doc(db, 'settings', 'departmentSettings');
    const settingsDoc = await getDoc(settingsRef);

    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      if (dayName) {
        const map = data.eventCapacities || {};
        const cap = map[dayName] || data.eventCapacity;
        console.log('Found capacity for day', dayName, '=>', cap);
        return cap || 55;
      }
      const capacity = data.eventCapacity;
      console.log('Found global capacity in Firebase:', capacity);
      return capacity || 55;
    }

    console.log('Settings document does not exist, using default capacity: 55');
    return 55; // Default capacity
  } catch (error) {
    console.error('Error fetching event capacity:', error);
    return 55; // Fallback to default capacity
  }
};

/**
 * Submit Industrial Visit registration
 * @param {Object} data - { fullName, email, contactNumber, branchYear, notes }
 * @returns {Promise<string>} document id
 */
export const submitIndustrialVisitRegistration = async (formData) => {
  try {
    // Get current capacity from Firebase settings
    const maxCapacity = await getEventCapacity(formData.selectedDay);
    console.log('Using capacity for registration validation:', maxCapacity);

    // Check registration count before submitting
    const currentCount = await getRegistrationCountForDay(formData.selectedDay);
    console.log(`Current registrations for ${formData.selectedDay}: ${currentCount}/${maxCapacity}`);

    if (currentCount >= maxCapacity) {
      throw new Error(`Registrations are full for ${formData.selectedDay} (${currentCount}/${maxCapacity}).`);
    }

    // Check for duplicate entries in the SAME EVENT (avoid composite indexes)
    const industrialVisitsRef = collection(db, IV_COLLECTION);
    const dayQuery = query(industrialVisitsRef, where('selectedDay', '==', formData.selectedDay));
    const daySnapshot = await getDocs(dayQuery);
    const normalizedEmail = formData.email.toLowerCase();
    const normalizedPhone = (formData.contactNumber || '').replace(/\D/g, '');
    const normalizedEnrollment = (formData.enrollmentNo || '').toUpperCase();
    const dup = daySnapshot.docs.find(d => {
      const data = d.data();
      const phone = (data.contactNumber || '').replace(/\D/g, '');
      const enr = (data.enrollmentNo || '').toUpperCase();
      return data.email === normalizedEmail || phone === normalizedPhone || enr === normalizedEnrollment;
    });
    if (dup) {
      const d = dup.data();
      if (d.email === normalizedEmail) throw new Error(`This email has already been registered for ${formData.selectedDay}.`);
      const phone = (d.contactNumber || '').replace(/\D/g, '');
      if (phone === normalizedPhone) throw new Error(`This phone number has already been registered for ${formData.selectedDay}.`);
      const enr = (d.enrollmentNo || '').toUpperCase();
      if (enr === normalizedEnrollment) throw new Error(`This enrollment number has already been registered for ${formData.selectedDay}.`);
    }

    // Add registration
    const registrationData = {
      ...formData,
      email: formData.email.toLowerCase(),
      submittedAt: serverTimestamp(),
      type: formData.type || (formData.selectedDay === 'Quiz Competition' ? 'quiz' : 'expert-talk')
    };

    await addDoc(industrialVisitsRef, registrationData);
    console.log('Registration submitted successfully');

    // Update registration count
    const countDocRef = doc(db, 'registrationCounts', formData.selectedDay);
    const countDoc = await getDoc(countDocRef);

    if (countDoc.exists()) {
      await setDoc(countDocRef, {
        count: increment(1)
      }, { merge: true });
    } else {
      await setDoc(countDocRef, {
        count: 1,
        dayName: formData.selectedDay
      });
    }

    // Send confirmation email based on event type
    try {
      const selectedDay = formData.selectedDay || '';

      // Determine which email to send based on the event
      if (selectedDay === 'Quiz Competition') {
        await sendQuizConfirmationEmail(formData);
        console.log('✅ Quiz confirmation email sent to:', formData.email);
      } else if (selectedDay.includes('IMC 25') || selectedDay.includes('IMC 2025')) {
        await sendIndustrialVisitConfirmationEmail(formData);
        console.log('✅ IMC 25 confirmation email sent to:', formData.email);
      } else if (selectedDay.includes('Ducat') || selectedDay.includes('Educational Visit')) {
        await sendDucatVisitConfirmationEmail(formData);
        console.log('✅ Ducat visit confirmation email sent to:', formData.email);
      } else {
        // For other events, send a generic confirmation
        console.log('ℹ️ No specific email template for:', selectedDay);
      }
    } catch (emailError) {
      console.error('❌ Failed to send confirmation email:', emailError);
      // Don't throw error - registration succeeded even if email fails
    }

  } catch (error) {
    console.error('Error submitting registration:', error);
    throw error;
  }
};

/**
 * Get current registration count
 * @returns {Promise<number>}
 */
export const getIndustrialVisitRegistrationCount = async () => {
  const snapshot = await getDocs(collection(db, IV_COLLECTION));
  return snapshot.size;
};

/**
 * Get registration count for a specific event day
 * @param {string} eventDay - The event day (e.g., "IMC 25 — Day 1")
 * @returns {Promise<number>}
 */
export const getRegistrationCountForDay = async (eventDay) => {
  try {
    // Get all documents and count manually to handle different field names
    const allDocs = await getDocs(collection(db, IV_COLLECTION));
    let count = 0;

    allDocs.forEach((doc) => {
      const data = doc.data();

      // Check all possible field names
      const dayFields = ['selectedDay', 'eventDay', 'day', 'selected_event', 'event_day'];

      for (const field of dayFields) {
        const fieldValue = data[field];
        if (!fieldValue) continue;

        // Check if this field value matches the requested day
        if (fieldValue === eventDay) {
          count++;
          break;
        }

        // Check for old format conversions
        const oldFormat = eventDay.replace('IMC 25 — ', 'IMC 2025 – ');
        const oldFormatHyphen = eventDay.replace('IMC 25 — ', 'IMC 2025 - ');
        const oldFormatFull = eventDay.replace('IMC 25', 'IMC 2025');

        if (fieldValue === oldFormat || fieldValue === oldFormatHyphen || fieldValue === oldFormatFull) {
          count++;
          break;
        }
      }
    });

    return count;
  } catch (error) {
    console.error('Error getting registration count:', error);

    // Return 0 if there's a connection error to prevent crashes
    if (error.code === 'unavailable' || error.code === 'permission-denied') {
      return 0;
    }

    throw new Error('Failed to get registration count for the selected day.');
  }
};

/**
 * Check if email already exists in registrations
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if email exists, false otherwise
 */
export const checkEmailExists = async (email) => {
  try {
    const q = query(collection(db, IV_COLLECTION), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    const exists = !querySnapshot.empty;
    return exists;
  } catch (error) {
    throw new Error('Failed to check email. Please try again.');
  }
};

/**
 * Check if email already exists GLOBALLY across all collections and events
 * @param {string} email - Email to check
 * @returns {Promise<boolean>} - True if email exists anywhere, false otherwise
 */
export const checkEmailExistsGlobally = async (email) => {
  try {
    const normalizedEmail = email.toLowerCase();

    // Check in industrial visit registrations collection
    const ivQuery = query(
      collection(db, IV_COLLECTION),
      where('email', '==', normalizedEmail)
    );
    const ivSnapshot = await getDocs(ivQuery);

    // Check in applications collection
    const appQuery = query(
      collection(db, 'applications'),
      where('email', '==', normalizedEmail)
    );
    const appSnapshot = await getDocs(appQuery);

    // Return true if email exists in ANY collection
    return !ivSnapshot.empty || !appSnapshot.empty;
  } catch (error) {
    console.error('Error checking email globally:', error);
    throw new Error('Failed to check email. Please try again.');
  }
};

/**
 * Check if phone number already exists GLOBALLY across all collections and events
 * @param {string} phoneNumber - Phone number to check
 * @returns {Promise<boolean>} - True if phone number exists anywhere, false otherwise
 */
export const checkPhoneNumberExistsGlobally = async (phoneNumber) => {
  try {
    // Remove all non-digit characters for consistent comparison
    const normalizedPhone = phoneNumber.replace(/\D/g, '');

    // Check in industrial visit registrations collection
    const ivSnapshot = await getDocs(collection(db, IV_COLLECTION));
    const phoneExistsInIV = ivSnapshot.docs.some(doc => {
      const data = doc.data();
      const existingPhone = data.contactNumber?.replace(/\D/g, '');
      return existingPhone === normalizedPhone;
    });

    // Check in applications collection
    const appSnapshot = await getDocs(collection(db, 'applications'));
    const phoneExistsInApp = appSnapshot.docs.some(doc => {
      const data = doc.data();
      const existingPhone = data.contactNumber?.replace(/\D/g, '');
      return existingPhone === normalizedPhone;
    });

    // Return true if phone exists in ANY collection
    return phoneExistsInIV || phoneExistsInApp;
  } catch (error) {
    console.error('Error checking phone number globally:', error);
    throw new Error('Failed to check phone number. Please try again.');
  }
};

/**
 * Check if enrollment number already exists GLOBALLY across all collections and events
 * @param {string} enrollmentNumber - Enrollment number to check
 * @returns {Promise<boolean>} - True if enrollment number exists anywhere, false otherwise
 */
export const checkEnrollmentNumberExistsGlobally = async (enrollmentNumber) => {
  try {
    // Normalize to uppercase for consistent comparison
    const normalizedEnrollment = enrollmentNumber.toUpperCase();

    // Check in industrial visit registrations collection
    const ivSnapshot = await getDocs(collection(db, IV_COLLECTION));
    const enrollmentExistsInIV = ivSnapshot.docs.some(doc => {
      const data = doc.data();
      const existingEnrollment = data.enrollmentNo?.toUpperCase();
      return existingEnrollment === normalizedEnrollment;
    });

    // Check in applications collection (if they have enrollment numbers)
    const appSnapshot = await getDocs(collection(db, 'applications'));
    const enrollmentExistsInApp = appSnapshot.docs.some(doc => {
      const data = doc.data();
      const existingEnrollment = data.enrollmentNo?.toUpperCase();
      return existingEnrollment === normalizedEnrollment;
    });

    // Return true if enrollment exists in ANY collection
    return enrollmentExistsInIV || enrollmentExistsInApp;
  } catch (error) {
    console.error('Error checking enrollment number globally:', error);
    throw new Error('Failed to check enrollment number. Please try again.');
  }
};

/**
 * Check if email already exists for a specific event day
 * @param {string} email - Email to check
 * @param {string} selectedDay - The event day (e.g., "Hands-on Session")
 * @returns {Promise<boolean>} - True if email exists for this day, false otherwise
 */
export const checkEmailExistsForDay = async (email, selectedDay) => {
  try {
    const normalizedEmail = email.toLowerCase();
    const q = query(
      collection(db, IV_COLLECTION),
      where('selectedDay', '==', selectedDay)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.some(d => (d.data().email || '').toLowerCase() === normalizedEmail);
  } catch (error) {
    console.error('Error checking email for day:', error);
    throw new Error('Failed to check email. Please try again.');
  }
};

/**
 * Check if phone number already exists for a specific event day
 * @param {string} phoneNumber - Phone number to check
 * @param {string} selectedDay - The event day (e.g., "Hands-on Session")
 * @returns {Promise<boolean>} - True if phone number exists for this day, false otherwise
 */
export const checkPhoneNumberExistsForDay = async (phoneNumber, selectedDay) => {
  try {
    const normalizedPhone = phoneNumber.replace(/\D/g, '');
    const q = query(
      collection(db, IV_COLLECTION),
      where('selectedDay', '==', selectedDay)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.some(doc => {
      const data = doc.data();
      const existingPhone = data.contactNumber?.replace(/\D/g, '');
      return existingPhone === normalizedPhone;
    });
  } catch (error) {
    console.error('Error checking phone number for day:', error);
    throw new Error('Failed to check phone number. Please try again.');
  }
};

/**
 * Check if enrollment number already exists for a specific event day
 * @param {string} enrollmentNumber - Enrollment number to check
 * @param {string} selectedDay - The event day (e.g., "Hands-on Session")
 * @returns {Promise<boolean>} - True if enrollment number exists for this day, false otherwise
 */
export const checkEnrollmentNumberExistsForDay = async (enrollmentNumber, selectedDay) => {
  try {
    const normalizedEnrollment = enrollmentNumber.toUpperCase();
    const q = query(
      collection(db, IV_COLLECTION),
      where('selectedDay', '==', selectedDay)
    );
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.some(doc => {
      const data = doc.data();
      const existingEnrollment = data.enrollmentNo?.toUpperCase();
      return existingEnrollment === normalizedEnrollment;
    });
  } catch (error) {
    console.error('Error checking enrollment number for day:', error);
    throw new Error('Failed to check enrollment number. Please try again.');
  }
};

/**
 * Check if email already exists for a specific event type
 * @param {string} email - Email to check
 * @param {string} eventType - Type of event (e.g., 'educational-visit', 'industrial-visit')
 * @returns {Promise<boolean>} - True if email exists for this event type, false otherwise
 */
export const checkEmailExistsForEventType = async (email, eventType) => {
  try {
    const q = query(
      collection(db, IV_COLLECTION),
      where('email', '==', email),
      where('type', '==', eventType)
    );
    const querySnapshot = await getDocs(q);
    const exists = !querySnapshot.empty;
    return exists;
  } catch (error) {
    throw new Error('Failed to check email. Please try again.');
  }
};

/**
 * Get all industrial visit registrations (for admin purposes)
 * @returns {Promise<Array>} - Array of all registrations
 */
export const getAllIndustrialVisitRegistrations = async () => {
  try {
    const q = query(collection(db, IV_COLLECTION), orderBy('submittedAt', 'desc'));
    const querySnapshot = await getDocs(q);
    const registrations = [];

    querySnapshot.forEach((doc) => {
      registrations.push({
        id: doc.id,
        ...doc.data()
      });
    });

    return registrations;
  } catch (error) {
    console.error('Error in getAllIndustrialVisitRegistrations:', error);

    // Fallback: If ordering fails, try fetching all and sort manually
    // This handles both 'submittedAt' and 'timestamp' fields
    try {
      console.log('Attempting fallback fetch without ordering...');
      const allDocs = await getDocs(collection(db, IV_COLLECTION));
      const registrations = [];

      allDocs.forEach((doc) => {
        const data = doc.data();
        registrations.push({
          id: doc.id,
          ...data,
          // Use submittedAt if exists, otherwise use timestamp
          submittedAt: data.submittedAt || data.timestamp
        });
      });

      // Sort manually by submittedAt (which might be timestamp)
      registrations.sort((a, b) => {
        const aTime = a.submittedAt?.seconds || 0;
        const bTime = b.submittedAt?.seconds || 0;
        return bTime - aTime;
      });

      console.log(`Fetched ${registrations.length} registrations (fallback mode)`);
      return registrations;
    } catch (fallbackError) {
      console.error('Fallback fetch also failed:', fallbackError);
      throw new Error('Failed to fetch industrial visit registrations.');
    }
  }
};

/**
 * Delete an industrial visit registration (for admin purposes)
 * @param {string} registrationId - The registration document ID
 * @returns {Promise<void>}
 */
export const deleteIndustrialVisitRegistration = async (registrationId) => {
  try {
    await deleteDoc(doc(db, IV_COLLECTION, registrationId));
  } catch (error) {
    throw new Error('Failed to delete industrial visit registration.');
  }
};

/**
 * Recalculate and fix registration counts for all event days
 * @returns {Promise<Object>} Object with day names and their corrected counts
 */
export const recalculateRegistrationCounts = async () => {
  try {
    console.log('Starting registration count recalculation...');

    // Get all registrations
    const allDocs = await getDocs(collection(db, IV_COLLECTION));
    const countByDay = {};

    // Count registrations per day
    allDocs.forEach((doc) => {
      const data = doc.data();
      const day = data.selectedDay;
      if (day) {
        countByDay[day] = (countByDay[day] || 0) + 1;
      }
    });

    console.log('Actual counts by day:', countByDay);

    // Update registrationCounts collection
    for (const [dayName, count] of Object.entries(countByDay)) {
      const countDocRef = doc(db, 'registrationCounts', dayName);
      await setDoc(countDocRef, {
        count: count,
        dayName: dayName
      });
      console.log(`Updated ${dayName}: ${count}`);
    }

    console.log('Registration counts successfully recalculated and updated!');
    return countByDay;
  } catch (error) {
    console.error('Error recalculating counts:', error);
    throw new Error('Failed to recalculate registration counts.');
  }
};

/**
 * Migrate old entries with 'timestamp' field to 'submittedAt'
 * @returns {Promise<number>} Number of migrated entries
 */
export const migrateTimestampToSubmittedAt = async () => {
  try {
    console.log('Starting migration of timestamp to submittedAt...');

    // Get all documents
    const allDocs = await getDocs(collection(db, IV_COLLECTION));
    let migratedCount = 0;

    for (const docSnapshot of allDocs.docs) {
      const data = docSnapshot.data();

      // Check if document has 'timestamp' but not 'submittedAt'
      if (data.timestamp && !data.submittedAt) {
        console.log(`Migrating document ${docSnapshot.id}...`);

        // Update the document to rename timestamp to submittedAt
        await setDoc(doc(db, IV_COLLECTION, docSnapshot.id), {
          submittedAt: data.timestamp,
          timestamp: null // Remove the old field
        }, { merge: true });

        migratedCount++;
      }
    }

    console.log(`Migration complete. Migrated ${migratedCount} documents.`);
    return migratedCount;
  } catch (error) {
    console.error('Migration error:', error);
    throw new Error('Failed to migrate timestamp fields.');
  }
};

/**
 * Delete all Expert Talk and Educational Visit registrations
 * @returns {Promise<number>} Number of deleted registrations
 */
export const deleteAllDucatVisitRegistrations = async () => {
  try {
    console.log('Starting deleteAllDucatVisitRegistrations...');

    // Get ALL documents from the industrial visit collection
    const allDocsSnapshot = await getDocs(collection(db, IV_COLLECTION));
    console.log(`Found ${allDocsSnapshot.size} documents in collection: ${IV_COLLECTION}`);

    if (allDocsSnapshot.size === 0) {
      console.log('No documents found to delete.');
      return 0;
    }

    // Log some document details for debugging
    const sampleDocs = allDocsSnapshot.docs.slice(0, 3);
    console.log('Sample documents to delete:', sampleDocs.map(doc => ({
      id: doc.id,
      data: doc.data()
    })));

    // Try deleting documents one by one with detailed error handling
    let deletedCount = 0;
    let failedDeletes = [];

    for (const docRef of allDocsSnapshot.docs) {
      try {
        console.log(`Attempting to delete document: ${docRef.id}`);
        await deleteDoc(docRef.ref);
        deletedCount++;
        console.log(`Successfully deleted document: ${docRef.id}`);
      } catch (deleteError) {
        console.error(`Failed to delete document ${docRef.id}:`, deleteError);
        failedDeletes.push({ id: docRef.id, error: deleteError.message });
      }
    }

    console.log(`Delete operation completed. Success: ${deletedCount}, Failed: ${failedDeletes.length}`);

    if (failedDeletes.length > 0) {
      console.error('Failed deletes:', failedDeletes);
    }

    // Verify deletion by fetching again
    const verifySnapshot = await getDocs(collection(db, IV_COLLECTION));
    console.log(`Verification: ${verifySnapshot.size} documents remaining after deletion.`);

    if (verifySnapshot.size > 0) {
      console.error('ERROR: Some documents were not deleted!');
      // Log remaining documents
      verifySnapshot.docs.forEach(doc => {
        console.log('Remaining document:', { id: doc.id, data: doc.data() });
      });
      throw new Error(`Failed to delete all documents. ${verifySnapshot.size} documents still remain.`);
    }

    console.log(`Successfully deleted all ${allDocsSnapshot.size} documents.`);
    return allDocsSnapshot.size;
  } catch (error) {
    console.error('Delete all error:', error);
    throw new Error(`Failed to delete all visit registrations: ${error.message}`);
  }
};

/**
 * Check if phone number already exists in registrations
 * @param {string} phoneNumber - Phone number to check
 * @param {string} [eventType=null] - Optional event type to filter by
 * @returns {Promise<boolean>} - True if phone number exists, false otherwise
 */
export const checkPhoneNumberExists = async (phoneNumber, eventType = null) => {
  try {
    // Remove all non-digit characters for consistent comparison
    const normalizedPhone = phoneNumber.replace(/\D/g, '');

    // Query the database for this phone number
    const industrialVisitRef = collection(db, IV_COLLECTION);
    let q = query(industrialVisitRef);

    // Add event type filter if provided
    if (eventType) {
      q = query(industrialVisitRef, where('type', '==', eventType));
    }

    // Get all documents and filter manually (since we need to normalize the phone numbers)
    const querySnapshot = await getDocs(q);

    // Check if any registration has this phone number
    return querySnapshot.docs.some(doc => {
      const data = doc.data();
      const existingPhone = data.contactNumber?.replace(/\D/g, '');
      return existingPhone === normalizedPhone;
    });

  } catch (error) {
    console.error('Error checking phone number:', error);
    throw error;
  }
};

/**
 * Check if enrollment number already exists in registrations
 * @param {string} enrollmentNumber - Enrollment number to check
 * @param {string} [eventType=null] - Optional event type to filter by
 * @returns {Promise<boolean>} - True if enrollment number exists, false otherwise
 */
export const checkEnrollmentNumberExists = async (enrollmentNumber, eventType = null) => {
  try {
    // Normalize to uppercase for consistent comparison
    const normalizedEnrollment = enrollmentNumber.toUpperCase();

    // Query the database for this enrollment number
    const industrialVisitRef = collection(db, IV_COLLECTION);
    let q;

    // Add event type filter if provided
    if (eventType) {
      q = query(industrialVisitRef, where('type', '==', eventType));
    } else {
      q = query(industrialVisitRef);
    }

    // Get all documents and filter manually
    const querySnapshot = await getDocs(q);

    // Check if any registration has this enrollment number
    return querySnapshot.docs.some(doc => {
      const data = doc.data();
      const existingEnrollment = data.enrollmentNo?.toUpperCase();
      return existingEnrollment === normalizedEnrollment;
    });

  } catch (error) {
    console.error('Error checking enrollment number:', error);
    throw error;
  }
};

/**
 * Update the attended status for an industrial visit registration
 * @param {string} registrationId - The registration document ID
 * @param {boolean} attended - The attended status
 * @returns {Promise<void>}
 */
export const updateAttendedStatus = async (registrationId, attended) => {
  try {
    const docRef = doc(db, IV_COLLECTION, registrationId);
    await updateDoc(docRef, {
      attended: attended,
      attendedUpdatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating attended status:', error);
    throw new Error('Failed to update attended status.');
  }
};





